'use client'

import useI18n from 'Hooks/useI18n'
import { CodeBlock, InlineCode, ScrollableBlock } from './GuideShared'
import GuideModalShell from './GuideModalShell'
import { getOfficialExampleHtml } from './officialExampleHtml'

type CreationGuideModalProps = {
  open: boolean
  onClose: () => void
}

function Page1() {
  const { t } = useI18n()

  const exampleCode = getOfficialExampleHtml(t)

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('How to Create a Tagline')}
      </h2>
      <div className="mt-4 space-y-3 text-sm leading-6 text-white/70">
        <p>
          {t(
            'Tagline is the character introduction card, displayed at the top of the chat page, the first thing users see when entering a chat.'
          )}
        </p>
        <p>
          {t(
            'You can use plain text, or create stunning visual effects with HTML.'
          )}
        </p>
        <p>
          <span className="font-semibold text-white">{t('Format rules:')}</span>{' '}
          {t(
            'If your HTML contains JavaScript code, you must wrap it with <html-box></html-box> tags. JS can only run inside html-box.'
          )}
        </p>
        <p className="text-white/70">{t('Example structure:')}</p>
      </div>
      <div className="mt-3">
        <CodeBlock code={exampleCode} />
      </div>
    </div>
  )
}

function Page2() {
  const { t } = useI18n()
  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('Manual Replacement Tutorial (After Loading Example)')}
      </h2>
      <div className="mt-4 space-y-3 text-sm leading-6 text-white/70">
        <p>
          {t(
            'After clicking "Load Official Example", you can directly find and replace the annotation markers in the code.'
          )}
        </p>
        <p className="font-semibold text-white">
          {t('Replace: Change card background color')}
        </p>
        <p>
          {t(
            'Find /* === Card Background Color Replace Start === */ in the code, copy the content in the replacement code below to change to a dark blue style:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <InlineCode code="background: #121826;" />
      </div>
      <div className="mt-4 text-sm leading-6 text-white/70">
        <p>
          {t(
            'Also find /* === Card Border Color Replace Start === */, replace with:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <InlineCode code="border: 3px solid #6366f1;" />
      </div>
      <div className="mt-4 text-sm leading-6 text-white/70">
        <p>
          {t(
            'Effect: The card will change from vintage brown to dark blue style. You can preview the effect on the right side after replacement.'
          )}
        </p>
      </div>
    </div>
  )
}

function Page3() {
  const { t } = useI18n()

  const prompt = t('creation_guide_ai_prompt')

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('AI Writes Tagline for You (Official Prompt)')}
      </h2>
      <div className="mt-4 text-sm leading-6 text-white/70">
        <p>
          {t(
            "If you don't want to write HTML yourself, let AI generate it for you! Copy the Prompt below to any AI tool:"
          )}
        </p>
      </div>
      <div className="mt-3">
        <ScrollableBlock text={prompt} />
      </div>
      <div className="mt-3 text-sm text-white/50">
        {t(
          'Tip: Replace the character information above with your own character settings.'
        )}
      </div>
    </div>
  )
}

function Page4() {
  const { t } = useI18n()

  const tips = `1. ${t('Please center the character name')}
2. ${t('Please make text sections more distinct')}
3. ${t('I want a macaron color scheme')}
4. ${t('I want each text block to be collapsed and expandable on click')}
5. ${t('I want an audio player at the bottom, audio path: xxxxx')}
6. ${t('I want a butterfly flying animation on hover over the character intro')}
7. ${t('I want the character card to be taller and slimmer')}
8. ${t('I want a magic scroll feel')}
9. ${t('Make the entire character card feel like being sealed in ice')}
10. ${t('I want the page to be scrollable')}`

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('Advanced Requirements Reference (Official Tips)')}
      </h2>
      <div className="mt-4 text-sm leading-6 text-white/70">
        <p>
          {t(
            'After generating the basic card, you can use the following requirements to further optimize:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <ScrollableBlock text={tips} />
      </div>
    </div>
  )
}

const PAGES = [Page1, Page2, Page3, Page4]

export default function CreationGuideModal({
  open,
  onClose,
}: CreationGuideModalProps) {
  return <GuideModalShell open={open} onClose={onClose} pages={PAGES} />
}
